// src/pages/admin/AusenciasCalendario.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import PageHeader from "pages/components/PageHeader";
import CloseIcon from "@mui/icons-material/Close";

import DatePicker from "react-multi-date-picker";
import "react-multi-date-picker/styles/colors/teal.css";
import "react-multi-date-picker/styles/layouts/mobile.css";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  IconButton,
  Card,
  Popover,
} from "@mui/material";

// FullCalendar
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import ptLocale from "@fullcalendar/core/locales/pt";

// ======================= Endpoints =======================
const EP = {
  FER_SelMes: "Feriados/SelMes",
  CAP_SelMes: "FuncoesCapacidadeDia/SelMes",
  AUS_SelCal: "Ausencias/SelCalendarioFuncao",
  AUS_Prever: "Ausencias/PreverCapacidade",
  AUS_Ins: "Ausencias/Ins",
  AUS_ValidarCheck: "Ausencias/ValidarCheck",
  AUS_Validar: "Ausencias/Validar",
  AUS_Rejeitar: "Ausencias/Rejeitar",
  AUS_UpdPeriodo: "Ausencias/UpdPeriodo",
  AUS_Del: "Ausencias/Del",
  FUN_Sel: "AusenciasFuncoes/SelFuncoesBaseAusencias",
  AUS_Cancelar: "Ausencias/Cancelar",
};

// ======================= Cores / Estilos =======================
const corPendente = "#f2994a";
const corValidado = "#27ae60";
const corRejeitado = "#c0392b";
const bgFeriado = "#e6e8e9";
const bgIndisp = "#f3f3f3";

const addDays = (isoDate, days) => {
  const d = new Date(isoDate);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10); // 'YYYY-MM-DD'
};

const toISO = (d) => {
  const dt = new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};

const isWeekend = (d) => {
  const dt = new Date(d);
  const day = dt.getDay(); // 0=domingo, 6=sábado
  return day === 0 || day === 6;
};

const MOTIVOS_OPCOES = [
  { value: "FERIAS", label: "FÉRIAS", autoFerias: true },
  { value: "FERIAS_ANTERIORES", label: "FÉRIAS DE ANOS ANTERIORES", autoFerias: true },

  { value: "BAIXA_MEDICA", label: "BAIXA MÉDICA", autoFerias: false },
  { value: "CONSULTA_MEDICA", label: "CONSULTA MÉDICA", autoFerias: false },
  { value: "PATERNIDADE_MATERNIDADE", label: "PATERNIDADE / MATERNIDADE", autoFerias: false },

  { value: "ASSUNTO_PESSOAL", label: "ASSUNTO PESSOAL", autoFerias: null },
  { value: "ACOMPANHAMENTO_FAMILIAR", label: "ACOMPANHAMENTO DE FAMILIAR", autoFerias: null },
  { value: "OUTROS", label: "OUTROS MOTIVOS", autoFerias: null },
];

const getMotivoOpt = (value) => MOTIVOS_OPCOES.find((x) => x.value === value);

const getMotivoLabel = (value) => getMotivoOpt(value)?.label || "";

export default function AusenciasCalendario() {
  const { showAlert } = useAlert();
  const { userData, temFuncao } = useAuth();

  const isAdminLike =
    temFuncao("Administração") || temFuncao("Informática") || temFuncao("Tesouraria");

  const podeValidarAusencias =
    isAdminLike ||
    (Array.isArray(userData?.funcoes) &&
      userData.funcoes.some((f) => (f || "").toLowerCase().startsWith("gestão ausências"))) ||
    (Array.isArray(userData?.funcoes) &&
      userData.funcoes.some((f) => (f || "").toLowerCase().startsWith("gestao ausencias")));

  const podeVerOutrosUtilizadores = podeValidarAusencias;

  const location = useLocation();
  const calRef = useRef();
  const calContainerRef = useRef(null);

  // ======= Estado base =======
  const hoje = new Date();
  const [mesAno, setMesAno] = useState({ ano: hoje.getFullYear(), mes: hoje.getMonth() + 1 });
  const [funcoes, setFuncoes] = useState([]);
  const [idFuncao, setIdFuncao] = useState(0);

  // Tooltip/Popover p/ feriados
  const [tip, setTip] = useState({ open: false, anchor: null, text: "" });
  const closeTip = () => setTip({ open: false, anchor: null, text: "" });

  const [utilizadoresFuncao, setUtilizadoresFuncao] = useState([]);
  const [idUtilizadorSelecionado, setIdUtilizadorSelecionado] = useState("");

  // dados do mês
  const [feriados, setFeriados] = useState([]);
  const [feriadosMeses, setFeriadosMeses] = useState({});
  const [indisponiveis, setIndisponiveis] = useState([]);
  const [ausencias, setAusencias] = useState([]);

  // dialogs - novo
  const [dlgNovo, setDlgNovo] = useState(false);
  const [dlgNovoDatePickerKey, setDlgNovoDatePickerKey] = useState(0);
  const [novoDiasArr, setNovoDiasArr] = useState([]);

  // NOVO: motivo por combobox + texto só para "OUTROS MOTIVOS"
  const [novoMotivoTipo, setNovoMotivoTipo] = useState("FERIAS");
  const [novoMotivo, setNovoMotivo] = useState(""); // usado apenas quando tipo = OUTROS

  const [novoMeioDia, setNovoMeioDia] = useState(false);
  const [novoPeriodo, setNovoPeriodo] = useState("manha"); // "manha" | "tarde"
  const [novoFerias, setNovoFerias] = useState(true); // por defeito conta para férias

  // detalhe
  const [dlgDetalhe, setDlgDetalhe] = useState({ open: false, reg: null });
  const [deepLinkId, setDeepLinkId] = useState(0);

  // override validação
  const [dlgOverride, setDlgOverride] = useState({ open: false, check: [] });

  const [dlgCancelar, setDlgCancelar] = useState({ open: false, motivo: "" });

  const motivoOpt = getMotivoOpt(novoMotivoTipo);
  const isLocked = motivoOpt?.autoFerias !== null;

  // editar pedido (período)
  const [dlgEditar, setDlgEditar] = useState({
    open: false,
    id: null,
    fracao: 2, // 2=dia inteiro, 1=meio dia
    periodo: "manha", // usado quando fracao=1
  });

  const carregarUtilizadoresDaFuncao = async (id) => {
    try {
      const resp = await api.post("Ausencias/SelUtilizadoresPorFuncao", { idfuncao: id });
      const lista = Array.isArray(resp?.data?.data) ? resp.data.data : [];

      // console.log("lista utilizadores função", lista);

      let listaFinal = lista;

      if (!podeVerOutrosUtilizadores) {
        listaFinal = lista.filter((u) => String(u.id) === String(userData?.id));
      }

      setUtilizadoresFuncao(listaFinal);

      // Pré-seleção: se o utilizador autenticado pertence à lista, usa-o;
      // caso contrário, usa o primeiro da lista; senão fica vazio.
      const me = userData?.id;
      const existeMe = listaFinal.find((u) => String(u.id) === String(me));

      if (existeMe) setIdUtilizadorSelecionado(String(existeMe.id));
      else if (lista.length) setIdUtilizadorSelecionado(String(lista[0].id));
      else setIdUtilizadorSelecionado("");
    } catch (e) {
      showAlert(String(e), "error");
    }
  };

  // ======================= Carregamentos =======================
  useEffect(() => {
    (async () => {
      const [lista, err] = await handleApi(api.post(EP.FUN_Sel, {}));

      if (err) {
        console.error("Erro a carregar funções base ausências:", err);
        showAlert(err, "error");
        setFuncoes([]);
        return;
      }

      const arr = Array.isArray(lista) ? lista : [];
      setFuncoes(arr);

      // Se vier função na URL, respeitar essa primeiro
      const qs = new URLSearchParams(location.search);
      const idFuncaoUrl = parseInt(qs.get("idfuncao") || "0", 10);

      if (idFuncaoUrl > 0) {
        setIdFuncao(idFuncaoUrl);
        await carregarUtilizadoresDaFuncao(idFuncaoUrl);
      } else if (arr.length && !idFuncao) {
        const fDist = arr.find((f) => (f.designacao || "").toLowerCase().startsWith("distrib"));
        const f = fDist || arr[0];

        setIdFuncao(f.id);
        await carregarUtilizadoresDaFuncao(f.id);
      }
    })();
    // só no load inicial (não depende de idFuncao, senão entra em loop)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const qs = new URLSearchParams(location.search);

    const idUrl = parseInt(qs.get("id") || "0", 10);
    const idFuncaoUrl = parseInt(qs.get("idfuncao") || "0", 10);
    const anoUrl = parseInt(qs.get("ano") || "0", 10);
    const mesUrl = parseInt(qs.get("mes") || "0", 10);

    if (idUrl > 0) {
      setDeepLinkId(idUrl);
    }

    if (anoUrl > 0 && mesUrl >= 1 && mesUrl <= 12) {
      setMesAno({ ano: anoUrl, mes: mesUrl });
    }

    if (idFuncaoUrl > 0) {
      setIdFuncao(idFuncaoUrl);
    }
  }, [location.search]);

  useEffect(() => {
    if (!calContainerRef.current || !calRef.current) return;

    // Se o browser não tiver ResizeObserver, não fazemos nada
    if (typeof ResizeObserver === "undefined") return;

    const api = calRef.current.getApi?.();
    if (!api) return;

    const observer = new ResizeObserver(() => {
      api.updateSize();
    });

    observer.observe(calContainerRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const apiCal = calRef.current?.getApi?.();
    if (!apiCal) return;

    apiCal.gotoDate(new Date(mesAno.ano, mesAno.mes - 1, 1));
  }, [mesAno.ano, mesAno.mes]);

  // ⚠️ ALTERAÇÃO MINÍMA: carregar sempre o mês (para trazer feriados),
  // e capacidade/ausências só se existir função.
  useEffect(() => {
    carregarMes();
    if (idFuncao) carregarUtilizadoresDaFuncao(idFuncao);
  }, [idFuncao, mesAno.ano, mesAno.mes]);

  const carregarFeriadosMes = async (ano, mes) => {
    const chave = `${ano}-${String(mes).padStart(2, "0")}`;

    if (feriadosMeses[chave]) {
      return feriadosMeses[chave];
    }

    const resp = await api.post(EP.FER_SelMes, { ano, mes });
    const raw = resp?.data;
    const lista = Array.isArray(raw?.data) ? raw.data : [];

    setFeriadosMeses((prev) => ({
      ...prev,
      [chave]: lista,
    }));

    return lista;
  };

  const carregarMes = async () => {
    try {
      const ano = mesAno.ano;
      const mes = mesAno.mes;

      const promessas = [
        carregarFeriadosMes(ano, mes),
        idFuncao
          ? api.post(EP.CAP_SelMes, { idfuncao: idFuncao, ano, mes })
          : Promise.resolve({ data: { success: true, data: [] } }),
        idFuncao
          ? api.post(EP.AUS_SelCal, { idfuncao: idFuncao, ano, mes })
          : Promise.resolve({ data: { success: true, data: [] } }),
      ];

      const [ferLista, cap, aus] = await Promise.all(promessas);

      const capRaw = cap?.data;
      const ausRaw = aus?.data;

      const _fer = Array.isArray(ferLista) ? ferLista : [];
      const _cap = Array.isArray(capRaw?.data) ? capRaw.data : [];
      const _aus = Array.isArray(ausRaw?.data) ? ausRaw.data : [];

      setFeriados(_fer);
      setIndisponiveis(_cap);
      setAusencias(_aus);
    } catch (e) {
      console.error(e);
      showAlert("Falha ao carregar ausências.", "error");
    }
  };

  useEffect(() => {
    if (!deepLinkId) return;
    if (!Array.isArray(ausencias) || !ausencias.length) return;

    const raw = ausencias.find((a) => Number(a.id) === Number(deepLinkId));
    if (!raw) return;

    let periodo = Number(raw.periodo ?? 0);

    setDlgDetalhe({
      open: true,
      reg: { ...raw, periodo },
    });

    setDeepLinkId(0);
  }, [ausencias, deepLinkId]);

  // ======================= Eventos do calendário =======================
  const eventos = useMemo(() => {
    const ev = [];
    const _fer = Array.isArray(feriados) ? feriados : [];
    const _ind = Array.isArray(indisponiveis) ? indisponiveis : [];
    const _aus = Array.isArray(ausencias) ? ausencias : [];

    _fer.forEach((f) => {
      const tipo = (f.alcancetipo || "").toLowerCase();
      const isNacional = tipo === "nacional";
      const isMunicipal = tipo === "municipal";

      if (isNacional) {
        // 1) BACKGROUND do dia
        ev.push({
          id: `FN-BG-${f.id}`,
          title: "", // sem texto no background
          start: f.data,
          end: addDays(f.data, 1), // <<< dia seguinte (exclusivo)
          allDay: true,
          display: "background",
          backgroundColor: bgFeriado,
          extendedProps: { tipo: "feriado-nacional-bg" },
        });
        // 2) Etiqueta (linha de texto)
        ev.push({
          id: `FN-LB-${f.id}`,
          title: f.nome,
          start: f.data,
          allDay: true,
          classNames: ["fc-feriado-label", "fc-feriado-nacional-label"],
          extendedProps: { tipo: "feriado-nacional-label" },
        });
      } else if (isMunicipal) {
        // Municipal: só a etiqueta, mas com fundo cinza na linha
        const destinos = f.alcancetod ? `${f.alcancetod} — ` : "";
        ev.push({
          id: `FM-LB-${f.id}`,
          title: `${destinos}${f.nome}`,
          start: f.data,
          allDay: true,
          classNames: ["fc-feriado-label", "fc-feriado-municipal-label"],
          extendedProps: { tipo: "feriado-municipal" },
        });
      } else {
        // fallback: etiqueta simples (sem fundo do dia)
        ev.push({
          id: `F-LB-${f.id}`,
          title: f.nome,
          start: f.data,
          allDay: true,
          classNames: ["fc-feriado-label"],
          extendedProps: { tipo: "feriado" },
        });
      }
    });

    _ind.forEach((l) => {
      if ((l.limitedia ?? 0) === 0) {
        ev.push({
          id: `I-${l.id}`,
          title: "Indisponível",
          start: l.dia,
          end: addDays(l.dia, 1), // <<< dia seguinte
          allDay: true,
          display: "background",
          backgroundColor: bgIndisp,
          extendedProps: { tipo: "indisp" },
        });
      }
    });

    _aus.forEach((a) => {
      const cor = a.estado === 1 ? corValidado : a.estado === 2 ? corRejeitado : corPendente;

      let periodoVal = Number(a.periodo ?? 0); // 0 | 1 | 2
      const isMeioDia = a.fracao === 1;

      const periodoTxt =
        isMeioDia && (periodoVal === 1 || periodoVal === 2)
          ? periodoVal === 1
            ? " (manhã)"
            : " (tarde)"
          : "";

      const tit = `${a.utilizador}${isMeioDia ? " (1/2)" : ""}${periodoTxt}`;

      const isCancelado = Number(a.cancelado ?? 0) === 1;

      ev.push({
        id: `A-${a.id}`,
        title: tit,
        start: a.dia,
        color: cor,
        classNames: isCancelado ? ["fc-ausencia-cancelada"] : [],
        extendedProps: {
          tipo: "ausencia",
          cancelado: isCancelado,
          motivo_cancelamento: a.motivo_cancelamento ?? "",
          raw: { ...a, periodo: periodoVal },
        },
      });
    });

    return ev;
  }, [feriados, indisponiveis, ausencias]);

  // ======================= Handlers calendário =======================
  const onDatesSet = (arg) => {
    // Para month-view, este é o 1º dia do mês *do título* (ex.: 2025-11-01)
    const d = arg.view?.currentStart ?? arg.start;
    const ano = d.getFullYear();
    const mes = d.getMonth() + 1; // 1..12

    setMesAno((s) => (s.ano === ano && s.mes === mes ? s : { ano, mes }));
  };

  const onEventClick = (info) => {
    const e = info.event;
    const tipo = e.extendedProps?.tipo || "";

    // Etiquetas de feriado (nacionais e municipais) → abrir popover com o nome completo
    if (tipo.startsWith("feriado-") || tipo === "feriado") {
      setTip({ open: true, anchor: info.el, text: e.title });
      return;
    }

    if (tipo !== "ausencia") return;

    const raw = e.extendedProps.raw || {};
    let periodo = raw.periodo ?? 0;
    if (!periodo || periodo === 0) {
      const t = (e.title || "").toLowerCase();
      if (t.includes("manhã") || t.includes("manha")) periodo = 1;
      else if (t.includes("tarde")) periodo = 2;
    }

    setDlgDetalhe({ open: true, reg: { ...raw, periodo } });
  };

  // ======================= Novo pedido =======================
  const abrirNovo = () => {
    setNovoDiasArr([]);

    setNovoMotivoTipo("FERIAS");
    setNovoMotivo("");

    setNovoMeioDia(false);
    setNovoPeriodo("manha");
    setNovoFerias(true);

    setDlgNovoDatePickerKey((k) => k + 1);
    setDlgNovo(true);
  };

  const gravarNovo = async () => {
    try {
      const dias = (novoDiasArr || [])
        .map((d) => (typeof d === "string" ? d : d.format ? d.format("YYYY-MM-DD") : null))
        .filter(Boolean)
        .map((s) => {
          const [y, m, dd] = s.split("-").map(Number);
          return new Date(Date.UTC(y, m - 1, dd));
        });

      if (!dias.length) return showAlert("Selecione pelo menos um dia.", "warning");

      const finsDeSemana = dias.filter((d) => isWeekend(d)).map((d) => toISO(d));
      if (finsDeSemana.length) {
        showAlert(
          `Não é permitido marcar ausências em sábados ou domingos: ${finsDeSemana.join(", ")}`,
          "warning"
        );
        return;
      }

      // Motivo vem da combobox; só exige texto se for "OUTROS MOTIVOS"
      if (novoMotivoTipo === "OUTROS") {
        if (!novoMotivo?.trim()) {
          showAlert("Indique o motivo do pedido (Outros motivos).", "warning");
          return;
        }
      }

      const prev = await api.post(EP.AUS_Prever, { idfuncao: idFuncao, dias });
      const previsao = prev?.data?.data || [];
      const indisps = previsao.filter((x) => x.indisponivel).map((x) => x.dia);
      if (indisps.length) {
        showAlert(
          `Alguns dias estão indisponíveis e serão ignorados: ${indisps.join(", ")}`,
          "warning"
        );
      }

      const periodoValor = novoMeioDia ? (novoPeriodo === "manha" ? 1 : 2) : null;

      const ins = await api.post(EP.AUS_Ins, {
        idutilizador: Number(idUtilizadorSelecionado || userData?.id),
        idfuncao_regra: idFuncao,
        dias,
        fracao: novoMeioDia ? 1 : 2,
        periodo: periodoValor, // 1=manhã, 2=tarde, null=dia inteiro
        ferias: novoFerias ? 1 : 0,
        motivo:
          novoMotivoTipo === "OUTROS"
            ? (novoMotivo || "").toUpperCase().trim()
            : getMotivoLabel(novoMotivoTipo),
      });

      const resp = ins?.data;

      if (!resp?.success) {
        showAlert(resp?.message || "Falha ao submeter pedido.", "error");
      } else {
        const tipo = resp?.type === "warning" ? "warning" : "success";
        showAlert(
          resp.message ||
            (tipo === "warning" ? "Pedido submetido com avisos." : "Pedido submetido."),
          tipo
        );

        setNovoDiasArr([]);
        setNovoMotivo("");
        setNovoMeioDia(false);
        setNovoPeriodo("manha");
        setNovoFerias(true);
        setDlgNovo(false);
        await carregarMes();
      }
    } catch {
      showAlert("Falha ao submeter pedido.", "error");
    }
  };

  // ======================= Validar / Rejeitar =======================
  const validar = async () => {
    if (!dlgDetalhe.reg) return;
    try {
      const check = await api.post(EP.AUS_ValidarCheck, { ids: [dlgDetalhe.reg.id] });
      const dados = check?.data?.data || [];
      const haIndisp = dados.some((x) => x.estado === "indisponivel");
      const haExcede = dados.some((x) => x.estado === "excede");

      if (haIndisp) {
        return showAlert("Dia indisponível. Não é possível validar.", "error");
      }
      if (haExcede) {
        setDlgOverride({ open: true, check: dados });
        return;
      }

      const val = await api.post(EP.AUS_Validar, {
        ids: [dlgDetalhe.reg.id],
        validado_por: userData?.id,
        allowOverride: 0,
      });
      const resp = val?.data;
      if (resp?.success) {
        showAlert(resp.message || "Validado.", "success");
        setDlgDetalhe({ open: false, reg: null });
        await carregarMes();
      } else showAlert(resp?.message || "Falha a validar.", "error");
    } catch {
      showAlert("Falha a validar.", "error");
    }
  };

  const confirmarOverride = async (motivo) => {
    try {
      const val = await api.post(EP.AUS_Validar, {
        ids: [dlgDetalhe.reg.id],
        validado_por: userData?.id,
        allowOverride: 1,
        motivoOverride: motivo || null,
      });
      const resp = val?.data;
      if (resp?.success) {
        showAlert(resp.message || "Validado com override.", "success");
        setDlgOverride({ open: false, check: [] });
        setDlgDetalhe({ open: false, reg: null });
        await carregarMes();
      } else showAlert(resp?.message || "Falha a validar.", "error");
    } catch {
      showAlert("Falha a validar.", "error");
    }
  };

  const rejeitar = async () => {
    if (!dlgDetalhe.reg) return;

    const motivo = (prompt("Motivo da rejeição (obrigatório):") || "").trim();
    if (!motivo) {
      showAlert("Indique o motivo da recusa.", "warning");
      return;
    }

    try {
      const r = await api.post(EP.AUS_Rejeitar, { ids: [dlgDetalhe.reg.id], motivo });
      const resp = r?.data;
      if (resp?.success) {
        showAlert(resp.message || "Rejeitado.", "success");
        setDlgDetalhe({ open: false, reg: null });
        await carregarMes();
      } else showAlert(resp?.message || "Falha a rejeitar.", "error");
    } catch {
      showAlert("Falha a rejeitar.", "error");
    }
  };

  const cancelarPedido = async () => {
    if (!dlgDetalhe.reg) return;

    const motivo = (dlgCancelar.motivo || "").trim();
    if (!motivo) {
      showAlert("Indique o motivo do cancelamento (obrigatório).", "warning");
      return;
    }

    try {
      const r = await api.post(EP.AUS_Cancelar, {
        id: dlgDetalhe.reg.id,
        idutilizador: userData?.id,
        motivo,
      });

      const resp = r?.data;
      if (resp?.success) {
        showAlert(resp.message || "Pedido cancelado.", "success");
        setDlgCancelar({ open: false, motivo: "" });
        setDlgDetalhe({ open: false, reg: null });
        await carregarMes();
      } else {
        showAlert(resp?.message || "Falha ao cancelar.", "error");
      }
    } catch {
      showAlert("Falha ao cancelar.", "error");
    }
  };

  // ======================= Editar / Apagar (autor do pedido) =======================
  const podeGerirProprioPedido =
    dlgDetalhe.open &&
    dlgDetalhe.reg &&
    dlgDetalhe.reg.estado === 0 &&
    Number(dlgDetalhe.reg.idutilizador) === Number(userData?.id);

  const abrirEditar = () => {
    if (!podeGerirProprioPedido) return;
    const fracao = dlgDetalhe.reg.fracao === 1 ? 1 : 2;
    const periodo = fracao === 1 ? (dlgDetalhe.reg.periodo === 2 ? "tarde" : "manha") : "manha";
    setDlgEditar({ open: true, id: dlgDetalhe.reg.id, fracao, periodo });
  };

  const gravarEditar = async () => {
    try {
      const payload = {
        id: dlgEditar.id,
        fracao: dlgEditar.fracao, // 1 ou 2
        periodo: dlgEditar.fracao === 1 ? (dlgEditar.periodo === "manha" ? 1 : 2) : null,
      };
      const r = await api.post(EP.AUS_UpdPeriodo, payload);
      const resp = r?.data;
      if (resp?.success) {
        showAlert(resp.message || "Pedido atualizado.", "success");
        setDlgEditar({ open: false, id: null, fracao: 2, periodo: "manha" });
        setDlgDetalhe({ open: false, reg: null });
        await carregarMes();
      } else {
        showAlert(resp?.message || "Falha ao atualizar o pedido.", "error");
      }
    } catch {
      showAlert("Falha ao atualizar o pedido.", "error");
    }
  };

  const apagarPedido = async () => {
    if (!podeGerirProprioPedido) return;
    const ok = window.confirm("Confirma eliminar este pedido?");
    if (!ok) return;
    try {
      const r = await api.post(EP.AUS_Del, { ids: [dlgDetalhe.reg.id] });
      const resp = r?.data;
      if (resp?.success) {
        showAlert(resp.message || "Pedido eliminado.", "success");
        setDlgDetalhe({ open: false, reg: null });
        await carregarMes();
      } else {
        showAlert(resp?.message || "Falha ao eliminar o pedido.", "error");
      }
    } catch {
      showAlert("Falha ao eliminar o pedido.", "error");
    }
  };

  useEffect(() => {
    const css = `
        /* etiqueta base */
        .fc .fc-daygrid-event.fc-feriado-label .fc-event-main{
        font-weight:600;font-size:.78rem;line-height:1.1rem;padding:2px 6px;border-radius:6px;
        }

        /* MUNICIPAL: mantém a faixa cinzenta na linha */
        .fc .fc-feriado-municipal-label .fc-event-main{background:#e6e8e9;color:#1b4f72;border:0;}

        /* NACIONAL: sem chip — a célula já tem o fundo cinzento */
        .fc .fc-feriado-nacional-label .fc-event-main{background:#e6e8e9;color:#1b4f72;border:0;padding:0 2px}

        /* Sábados e Domingos com o mesmo fundo dos feriados */
        .fc-day-sat,
        .fc-day-sun {
          background: #e6e8e9 !important;
        }

        /* AUSÊNCIAS CANCELADAS: mantém cor, mas risca e reduz opacidade */
        .fc .fc-daygrid-event.fc-ausencia-cancelada .fc-event-title,
        .fc .fc-daygrid-event.fc-ausencia-cancelada .fc-event-time,
        .fc .fc-daygrid-event.fc-ausencia-cancelada .fc-event-main {
          text-decoration: line-through;
          opacity: 0.65;
        }

        /* remover quaisquer contornos/bordas azuis
           e deixar o texto "colado" ao rectângulo de fundo */
        .fc .fc-feriado-label a,
        .fc .fc-feriado-label a:focus,
        .fc .fc-feriado-label a:active,
        .fc .fc-feriado-label a:focus-visible{
          outline: none !important;
          box-shadow: none !important;
          border: 0 !important;
          background: transparent !important; /* só feriados */
        }

        /* fundo da CÉLULA para feriado nacional */
        .fc .dia-feriado-nacional{background:#e6e8e9!important}
        `;
    const el = document.createElement("style");
    el.id = "feriados-inline-css";
    el.innerHTML = css;
    document.head.appendChild(el);
    return () => {
      const n = document.getElementById("feriados-inline-css");
      n && n.remove();
    };
  }, []);

  const feriadosNacionaisSet = useMemo(() => {
    const s = new Set();

    Object.values(feriadosMeses || {}).forEach((lista) => {
      (lista || []).forEach((f) => {
        if ((f.alcancetipo || "").toLowerCase() === "nacional") {
          s.add(toISO(f.data));
        }
      });
    });

    return s;
  }, [feriadosMeses]);

  // ======================= Render =======================
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <PageHeader />
      <MDBox pt={1}>
        <Card>
          <MDBox p={3}>
            <MDBox display="flex" flexWrap="wrap" gap={2} alignItems="flex-end" mb={2}>
              <MDBox flex="1 1 220px" minWidth={180}>
                <div style={{ fontSize: 12, color: "#7b809a", marginBottom: 4 }}>Função</div>
                <select
                  value={idFuncao}
                  onChange={(e) => {
                    const v = parseInt(e.target.value || "0", 10);
                    setIdFuncao(v);
                    //carregarUtilizadoresDaFuncao(v);
                  }}
                  style={{
                    width: "100%",
                    padding: 10,
                    borderRadius: 8,
                    fontSize: 14,
                    border: "1px solid #ced4da",
                  }}
                >
                  <option value={0}>— selecione —</option>
                  {Array.isArray(funcoes) &&
                    funcoes.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.designacao}
                      </option>
                    ))}
                </select>
              </MDBox>

              <MDBox flex="1 1 220px" minWidth={180}>
                <div style={{ fontSize: 12, color: "#7b809a", marginBottom: 4 }}>Utilizador</div>
                <select
                  value={idUtilizadorSelecionado}
                  onChange={(e) => setIdUtilizadorSelecionado(e.target.value)}
                  disabled={!podeVerOutrosUtilizadores}
                  style={{
                    width: "100%",
                    padding: 10,
                    borderRadius: 8,
                    fontSize: 14,
                    border: "1px solid #ced4da",
                  }}
                >
                  {utilizadoresFuncao.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.designacao}
                    </option>
                  ))}
                </select>
              </MDBox>

              <MDButton
                color="success"
                onClick={abrirNovo}
                disabled={!idFuncao}
                sx={{ flex: "0 0 auto" }}
              >
                Novo pedido
              </MDButton>
            </MDBox>

            <div
              ref={calContainerRef}
              style={{
                background: "#fff",
                borderRadius: 12,
                boxShadow: "0 2px 12px rgba(0,0,0,.06)",
                padding: 12,
              }}
            >
              <MDBox mt={1.5} display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <span
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: 3,
                      background: corPendente,
                      display: "inline-block",
                    }}
                  />
                  <span style={{ fontSize: 12, color: "#556" }}>Pendente</span>
                </span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <span
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: 3,
                      background: corValidado,
                      display: "inline-block",
                    }}
                  />
                  <span style={{ fontSize: 12, color: "#556" }}>Validado</span>
                </span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <span
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: 3,
                      background: corRejeitado,
                      display: "inline-block",
                    }}
                  />
                  <span style={{ fontSize: 12, color: "#556" }}>Recusado</span>
                </span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <span
                    style={{
                      width: 16,
                      height: 12,
                      borderRadius: 3,
                      background: bgFeriado,
                      display: "inline-block",
                    }}
                  />
                  <span style={{ fontSize: 12, color: "#556" }}>
                    Feriado nacional (cor no dia) ou feriado municipal (cor apenas na linha)
                  </span>
                </span>
              </MDBox>
              <FullCalendar
                locales={[ptLocale]}
                locale="pt"
                dayCellClassNames={(arg) => {
                  return feriadosNacionaisSet.has(toISO(arg.date)) ? ["dia-feriado-nacional"] : [];
                }}
                ref={calRef}
                plugins={[dayGridPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                height="auto"
                events={eventos}
                datesSet={onDatesSet}
                eventClick={onEventClick}
                firstDay={1}
                dayMaxEventRows={3}
                eventDidMount={(info) => {
                  // pointer em todos os eventos com texto (não-background)
                  const hasText = (info.event.title || "").trim().length > 0;
                  const isBg = info.event.display === "background";
                  if (hasText && !isBg) {
                    info.el.style.cursor = "pointer";
                  }
                }}
              />
            </div>
          </MDBox>
        </Card>
      </MDBox>

      {/* Dialog Novo Pedido */}
      <Dialog
        open={dlgNovo}
        onClose={() => setDlgNovo(false)}
        fullWidth
        maxWidth="sm"
        scroll="paper"
        PaperProps={{
          sx: {
            maxHeight: "70vh",
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle>
          Novo pedido
          <IconButton
            onClick={() => setDlgNovo(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            pt: 2,
            pb: 4,
            minHeight: 300,
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          <MDBox mb={2}>
            <label>Motivo</label>
            <div style={{ marginTop: 6 }}>
              <select
                value={novoMotivoTipo}
                onChange={(e) => {
                  const v = e.target.value;
                  setNovoMotivoTipo(v);

                  const opt = getMotivoOpt(v);

                  if (opt?.autoFerias === true) setNovoFerias(true);
                  if (opt?.autoFerias === false) setNovoFerias(false);

                  if (v !== "OUTROS") setNovoMotivo("");
                }}
                style={{ padding: 8, borderRadius: 8, fontSize: 14, minWidth: 280 }}
              >
                {MOTIVOS_OPCOES.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </MDBox>

          {novoMotivoTipo === "OUTROS" && (
            <MDBox mb={2}>
              <label>Motivo (obrigatório)</label>
              <TextField
                fullWidth
                value={novoMotivo}
                onChange={(e) => setNovoMotivo((e.target.value || "").toUpperCase())}
                placeholder="Ex.: EXPLICAR MOTIVO"
              />
            </MDBox>
          )}

          {!getMotivoOpt(novoMotivoTipo)?.hideCheckbox && (
            <MDBox mb={1.5}>
              <div style={{ marginTop: 6 }}>
                <FormControlLabel
                  disabled={isLocked}
                  control={
                    <Checkbox
                      checked={novoFerias}
                      onChange={(e) => setNovoFerias(e.target.checked)}
                      disabled={isLocked}
                    />
                  }
                  label={
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                      <span style={{ color: isLocked ? "#9e9e9e" : "inherit" }}>
                        Conta para férias
                      </span>
                      {isLocked && <span style={{ color: "#9e9e9e", fontSize: 12 }}>(fixo)</span>}
                    </span>
                  }
                  sx={{
                    "&.Mui-disabled": { cursor: "not-allowed" },
                  }}
                />
              </div>
            </MDBox>
          )}

          <MDBox mb={2}>
            <label>Tipo</label>
            <div style={{ marginTop: 6 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={novoMeioDia}
                    onChange={(e) => setNovoMeioDia(e.target.checked)}
                  />
                }
                label="Meio dia"
              />
            </div>

            {novoMeioDia && (
              <div style={{ marginTop: 8 }}>
                <label>Período</label>
                <br />
                <select
                  value={novoPeriodo}
                  onChange={(e) => setNovoPeriodo(e.target.value)}
                  style={{ padding: 8, borderRadius: 8, fontSize: 14, minWidth: 160 }}
                >
                  <option value="manha">Manhã</option>
                  <option value="tarde">Tarde</option>
                </select>
              </div>
            )}
          </MDBox>

          <MDBox mb={2}>
            <label>Dias</label>
            <div style={{ marginTop: 6 }}>
              <DatePicker
                key={dlgNovoDatePickerKey}
                multiple
                value={novoDiasArr}
                onChange={setNovoDiasArr}
                format="YYYY-MM-DD"
                weekStartDayIndex={1}
                locale="pt"
                className="teal"
                calendarPosition="bottom-left"
                offsetY={8}
                onMonthChange={async (dateObject) => {
                  const ano = dateObject?.year;
                  const mes = dateObject?.month?.number;

                  if (ano && mes) {
                    await carregarFeriadosMes(ano, mes);
                  }
                }}
                mapDays={({ date }) => {
                  const jsDate = date?.toDate?.();
                  if (!jsDate) return {};

                  const iso = toISO(jsDate);

                  // --- fins de semana ---
                  const day = jsDate.getDay(); // 0=domingo, 6=sábado
                  const isFimSemana = day === 0 || day === 6;

                  // --- feriados nacionais ---
                  const isFeriadoNacional = feriadosNacionaisSet.has(iso);

                  if (isFimSemana || isFeriadoNacional) {
                    return {
                      disabled: true,
                      style: {
                        color: "#bdbdbd",
                        backgroundColor: isFeriadoNacional ? "#ffe9e9" : "#f3f3f3",
                        cursor: "not-allowed",
                      },
                    };
                  }

                  return {};
                }}
              />
            </div>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setDlgNovo(false)} color="secondary">
            Cancelar
          </MDButton>
          <MDButton onClick={gravarNovo} color="success">
            Submeter
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Dialog Detalhe */}
      <Dialog
        open={dlgDetalhe.open}
        onClose={() => setDlgDetalhe({ open: false, reg: null })}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Detalhe do pedido
          <IconButton
            onClick={() => setDlgDetalhe({ open: false, reg: null })}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {dlgDetalhe.reg && (
            <>
              <MDTypography>
                Utilizador: <b>{dlgDetalhe.reg.utilizador}</b>
              </MDTypography>

              <MDTypography>
                Dia: <b>{String(dlgDetalhe.reg.dia || "").split("T")[0]}</b>
              </MDTypography>

              <MDTypography>
                Período:{" "}
                <b>
                  {dlgDetalhe.reg.fracao === 1
                    ? dlgDetalhe.reg.periodo === 1
                      ? "Manhã"
                      : dlgDetalhe.reg.periodo === 2
                      ? "Tarde"
                      : "-"
                    : "Dia inteiro"}
                </b>
              </MDTypography>

              <MDTypography>
                Estado:{" "}
                <b>
                  {dlgDetalhe.reg.estado === 0
                    ? "Pendente"
                    : dlgDetalhe.reg.estado === 1
                    ? "Validado"
                    : "Rejeitado"}
                </b>
              </MDTypography>

              <MDTypography>
                Conta para férias: <b>{Number(dlgDetalhe.reg.ferias ?? 0) === 1 ? "Sim" : "Não"}</b>
              </MDTypography>

              {dlgDetalhe.reg.motivo && (
                <MDTypography mt={1}>Motivo: {dlgDetalhe.reg.motivo}</MDTypography>
              )}

              {Number(dlgDetalhe.reg.cancelado ?? 0) === 1 && (
                <MDBox mt={2} p={1.5} sx={{ borderRadius: 2, backgroundColor: "rgba(0,0,0,0.06)" }}>
                  <MDTypography variant="button" fontWeight="bold">
                    Cancelado
                  </MDTypography>
                  <MDTypography mt={0.5}>
                    Motivo: <b>{dlgDetalhe.reg.motivo_cancelamento || "-"}</b>
                  </MDTypography>
                </MDBox>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {/* editar/apagar – apenas autor e enquanto pendente */}
          {podeGerirProprioPedido && (
            <>
              <MDButton color="warning" onClick={abrirEditar}>
                Editar
              </MDButton>
              <MDButton color="error" onClick={apagarPedido}>
                Apagar
              </MDButton>
            </>
          )}

          {podeValidarAusencias && dlgDetalhe.reg?.estado === 0 && (
            <>
              <MDButton color="info" onClick={validar}>
                Validar
              </MDButton>
              <MDButton color="error" onClick={rejeitar}>
                Rejeitar
              </MDButton>
            </>
          )}

          {dlgDetalhe.reg?.estado === 1 && Number(dlgDetalhe.reg?.cancelado ?? 0) === 0 && (
            <MDButton color="error" onClick={() => setDlgCancelar({ open: true, motivo: "" })}>
              Cancelar pedido
            </MDButton>
          )}

          <MDButton color="secondary" onClick={() => setDlgDetalhe({ open: false, reg: null })}>
            Fechar
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Dialog Override */}
      <Dialog
        open={dlgOverride.open}
        onClose={() => setDlgOverride({ open: false, check: [] })}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Capacidade atingida</DialogTitle>
        <DialogContent dividers>
          <MDTypography mb={1}>A validação excede o limite em:</MDTypography>
          <ul>
            {dlgOverride.check.map((c, i) => (
              <li key={i}>
                {c.dia} — limite {c.limite}, já aprovados {c.aprovados}, pendentes{" "}
                {c.soma_pendentes}
              </li>
            ))}
          </ul>
          <MDTypography mt={2}>Pretende validar com override? (será registado)</MDTypography>
          <MDBox mt={2}>
            <label>Motivo do override (opcional)</label>
            <TextField fullWidth id="motivo-override" />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton color="secondary" onClick={() => setDlgOverride({ open: false, check: [] })}>
            Cancelar
          </MDButton>
          <MDButton
            color="warning"
            onClick={() => {
              const motivo = document.getElementById("motivo-override").value;
              confirmarOverride(motivo);
            }}
          >
            Validar com override
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Dialog Editar Período */}
      <Dialog
        open={dlgEditar.open}
        onClose={() => setDlgEditar({ open: false, id: null, fracao: 2, periodo: "manha" })}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Editar pedido</DialogTitle>
        <DialogContent dividers>
          <MDBox mb={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={dlgEditar.fracao === 1}
                  onChange={(e) =>
                    setDlgEditar((s) => ({ ...s, fracao: e.target.checked ? 1 : 2 }))
                  }
                />
              }
              label="Meio dia"
            />
          </MDBox>

          {dlgEditar.fracao === 1 && (
            <MDBox>
              <label>Período</label>
              <br />
              <select
                value={dlgEditar.periodo}
                onChange={(e) => setDlgEditar((s) => ({ ...s, periodo: e.target.value }))}
                style={{ padding: 8, borderRadius: 8, fontSize: 14, minWidth: 160 }}
              >
                <option value="manha">Manhã</option>
                <option value="tarde">Tarde</option>
              </select>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton
            color="secondary"
            onClick={() => setDlgEditar({ open: false, id: null, fracao: 2, periodo: "manha" })}
          >
            Cancelar
          </MDButton>
          <MDButton color="success" onClick={gravarEditar}>
            Gravar
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dlgCancelar.open}
        onClose={() => setDlgCancelar({ open: false, motivo: "" })}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Cancelar pedido validado</DialogTitle>
        <DialogContent dividers>
          <MDBox>
            <label>Motivo do cancelamento (obrigatório)</label>
            <TextField
              fullWidth
              multiline
              minRows={3}
              value={dlgCancelar.motivo}
              onChange={(e) => setDlgCancelar((s) => ({ ...s, motivo: e.target.value }))}
              placeholder="Ex.: Cancelado a pedido do colaborador"
            />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton color="secondary" onClick={() => setDlgCancelar({ open: false, motivo: "" })}>
            Voltar
          </MDButton>
          <MDButton color="error" onClick={cancelarPedido} disabled={!dlgCancelar.motivo.trim()}>
            Confirmar cancelamento
          </MDButton>
        </DialogActions>
      </Dialog>

      <Popover
        open={tip.open}
        anchorEl={tip.anchor}
        onClose={closeTip}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        disableRestoreFocus
        keepMounted
        sx={{
          ".MuiPaper-root": {
            borderRadius: 2,
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            color: "#fff",
            boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
          },
        }}
      >
        <MDBox p={1.2} maxWidth={300}>
          <MDTypography variant="button" color="white">
            {tip.text}
          </MDTypography>
        </MDBox>
      </Popover>
    </DashboardLayout>
  );
}
